import { useMemo, useState } from "react";
import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import useSWR from "swr";

import ExportCSV from "../../ExportCSV";
import { fetchStreetTeamReport, streetTeamReportConfigured } from "../../../services/streetTeamReport";
import StreetTeamChart, { buildStreetTeamBarChart } from "./StreetTeamChart";
import StreetTeamFilters from "./StreetTeamFilters";
import StreetTeamProvinceTables from "./StreetTeamProvinceTables";
import StreetTeamSummary from "./StreetTeamSummary";
import StreetTeamTable from "./StreetTeamTable";

const REPORT_CARD_SX = {
  width: "100%",
  boxShadow: "0 2px 4px rgba(0,0,0,0.5)",
  position: "relative",
  padding: "16px",
  backgroundColor: "#efeeee",
};

const swrOptions = { revalidateOnFocus: false, keepPreviousData: true };

const VARIANTS = {
  leaderboard: {
    title: "Street team / Leaderboard",
    description:
      "Who uploaded photos in the street-team folder during the selected date range. Each file is counted once. People with fewer than 3 uploads are left out, along with internal+cdnbeef@tangle.ca.",
    tableTitle: "Uploads by person",
    tableDescription: "Image count for each person who uploaded at least 3 photos in this range.",
    nameHeader: "Uploaded by",
    detailHeader: "Email",
    chartTitle: "Most images uploaded",
    chartSubtitle: "People with the most uploaded photos in this date range.",
    emptyMessage: "No image uploads in this date range.",
    fileSlug: "leaderboard",
  },
  retailer: {
    title: "Street team / Retailer",
    description:
      "Street-team folder photos linked to a price history retailer, grouped by province. One photo counts once for a retailer in that province, even when it produced several price rows. Visited by is who uploaded those photos.",
    nameHeader: "Retailer",
    detailHeader: "Visited by",
    chartTitle: "Images by province",
    chartSubtitle: "Provinces with the most uploaded photos in this date range.",
    emptyMessage: "No retailer visits in this date range.",
    fileSlug: "retailer",
  },
  cuts: {
    title: "Street team / Beef cuts",
    description:
      "Street-team folder photos linked to a price history beef cut. One photo counts once for a cut, even when several price rows name that cut. Visited by is who uploaded those photos.",
    tableTitle: "Images by beef cut",
    tableDescription: "Beef cuts in this date range, and who uploaded the photos.",
    nameHeader: "Beef cut",
    detailHeader: "Visited by",
    chartTitle: "Images by beef cut",
    chartSubtitle: "Cuts with the most uploaded photos in this date range.",
    emptyMessage: "No beef cut photos in this date range.",
    fileSlug: "beef-cuts",
  },
};

function formatCountLabel(row, { withProvince = false } = {}) {
  if (!row) {
    return "—";
  }
  const count = Number(row.count) || 0;
  const name =
    withProvince && row.provinceLabel ? `${row.label}, ${row.provinceLabel}` : row.label;
  return `${name} (${count.toLocaleString()})`;
}

function formatExportFileName(slug, dateFrom, dateTo) {
  const fromLabel = dateFrom?.isValid?.() ? dateFrom.format("YYYY-MM-DD") : "from";
  const toLabel = dateTo?.isValid?.() ? dateTo.format("YYYY-MM-DD") : "to";
  return `street-team-${slug}-${fromLabel}-to-${toLabel}.csv`;
}

function viewForVariant(variant, report) {
  if (!report) {
    return { rows: [], top: null, totalImages: 0, linkedImageCount: 0 };
  }
  if (variant === "leaderboard") {
    return report.leaderboard;
  }
  if (variant === "retailer") {
    return report.retailers;
  }
  return report.cuts;
}

function displayRows(variant, view) {
  return (view?.rows || []).map((row) => ({
    id: row.id,
    province: row.provinceLabel || "",
    label: row.label,
    detail: variant === "leaderboard" ? row.email || "" : (row.visitors || []).join(", "),
    count: row.count,
    images: variant === "leaderboard" ? row.images || [] : undefined,
  }));
}

export default function StreetTeamReport({ variant = "leaderboard" }) {
  const config = VARIANTS[variant] || VARIANTS.leaderboard;
  const [dateFrom, setDateFrom] = useState(() => dayjs().subtract(1, "month").startOf("day"));
  const [dateTo, setDateTo] = useState(() => dayjs().endOf("day"));

  const startDateForApi = dateFrom?.isValid?.() ? dateFrom.startOf("day").toISOString() : "";
  const endDateForApi = dateTo?.isValid?.() ? dateTo.endOf("day").toISOString() : "";
  const dateRangeIsValid = Boolean(
    startDateForApi &&
      endDateForApi &&
      dateFrom?.isValid() &&
      dateTo?.isValid() &&
      !dateFrom.startOf("day").isAfter(dateTo.endOf("day"))
  );
  const queriesEnabled = streetTeamReportConfigured() && dateRangeIsValid;

  const { data, error, isLoading, isValidating } = useSWR(
    queriesEnabled ? ["street-team-report", startDateForApi, endDateForApi] : null,
    () => fetchStreetTeamReport({ dateFrom: startDateForApi, dateTo: endDateForApi }),
    swrOptions
  );

  const view = viewForVariant(variant, data);
  const rows = useMemo(() => displayRows(variant, view), [variant, view]);
  const exportRows = useMemo(() => {
    if (variant !== "retailer") {
      return rows;
    }
    return (view?.provinces || []).flatMap((province) =>
      (province.retailers || []).map((row) => ({
        province: row.provinceLabel || province.label,
        label: row.label,
        detail: (row.visitors || []).join(", "),
        count: row.count,
      }))
    );
  }, [rows, variant, view]);
  const chart = useMemo(
    () => buildStreetTeamBarChart(variant === "retailer" ? view?.provinces || [] : view?.rows || []),
    [variant, view]
  );

  const stats = useMemo(() => {
    if (!data) {
      return [];
    }
    if (variant === "leaderboard") {
      return [
        { label: "Most images", value: formatCountLabel(view.top) },
        { label: "Images uploaded", value: Number(view.totalImages || 0).toLocaleString() },
        { label: "People", value: Number(view.rows?.length || 0).toLocaleString() },
      ];
    }
    if (variant === "retailer") {
      return [
        { label: "Most images", value: formatCountLabel(view.top, { withProvince: true }) },
        { label: "Provinces", value: Number(view.provinces?.length || 0).toLocaleString() },
        { label: "Retailers", value: Number(view.rows?.length || 0).toLocaleString() },
        { label: "Photos linked", value: Number(view.linkedImageCount || 0).toLocaleString() },
      ];
    }
    return [
      { label: "Most images", value: formatCountLabel(view.top) },
      { label: "Beef cuts", value: Number(view.rows?.length || 0).toLocaleString() },
      { label: "Photos linked", value: Number(view.linkedImageCount || 0).toLocaleString() },
    ];
  }, [data, variant, view]);

  const csvColumns =
    variant === "retailer"
      ? [
          { key: "province", label: "Province" },
          { key: "label", label: config.nameHeader },
          { key: "detail", label: config.detailHeader },
          { key: "count", label: "Images" },
        ]
      : [
          { key: "label", label: config.nameHeader },
          { key: "detail", label: config.detailHeader },
          { key: "count", label: "Images" },
        ];

  if (!streetTeamReportConfigured()) {
    return (
      <Box sx={{ py: 1 }}>
        <Alert severity="warning">
          Street team reports are not configured. Set `REACT_APP_PRICE_DIRECTUS_URL` and
          `REACT_APP_PRICE_DIRECTUS_TOKEN` for this environment, then reload the page.
        </Alert>
      </Box>
    );
  }

  const isInitialLoading = queriesEnabled && !error && data === undefined && isLoading;
  const isRefreshing = Boolean(isValidating && data && !error);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={REPORT_CARD_SX}>
        <StreetTeamFilters
          title={config.title}
          description={config.description}
          dateFrom={dateFrom}
          dateTo={dateTo}
          setDateFrom={setDateFrom}
          setDateTo={setDateTo}
          filterActions={
            <ExportCSV
              columns={csvColumns}
              data={exportRows}
              fileName={formatExportFileName(config.fileSlug, dateFrom, dateTo)}
              buttonText="Export to CSV"
              disabled={!dateRangeIsValid || exportRows.length === 0}
            />
          }
        />

        <Stack spacing={2} sx={{ mt: 2 }}>
          {!dateRangeIsValid ? (
            <Alert severity="warning">
              Select a valid From and To date range. From must be on or before To.
            </Alert>
          ) : null}

          {error ? (
            <Alert severity="error">
              {error.message || "Something went wrong while loading street team data."}
            </Alert>
          ) : null}

          {isRefreshing ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2">Loading records...</Typography>
            </Stack>
          ) : null}

          {data?.truncated ? (
            <Alert severity="warning">
              This date range has more records than the report loaded. Narrow the dates for a
              complete count.
            </Alert>
          ) : null}

          {variant !== "leaderboard" && data?.unmatchedImageCount > 0 ? (
            <Alert severity="info">
              {Number(data.unmatchedImageCount).toLocaleString()} uploaded{" "}
              {data.unmatchedImageCount === 1 ? "photo is" : "photos are"} not linked to a price
              history row, so {data.unmatchedImageCount === 1 ? "it is" : "they are"} not counted
              here.
            </Alert>
          ) : null}

          {dateRangeIsValid && !error ? (
            <>
              <StreetTeamSummary stats={stats} />
              <StreetTeamChart
                title={config.chartTitle}
                subtitle={config.chartSubtitle}
                chart={chart}
                isLoading={isInitialLoading || isRefreshing}
                error={error}
              />
              {variant === "retailer" ? (
                <StreetTeamProvinceTables
                  provinces={view?.provinces || []}
                  isLoading={isInitialLoading || isRefreshing}
                  emptyMessage={config.emptyMessage}
                />
              ) : (
                <StreetTeamTable
                  title={config.tableTitle}
                  description={config.tableDescription}
                  nameHeader={config.nameHeader}
                  detailHeader={config.detailHeader}
                  rows={rows}
                  isLoading={isInitialLoading || isRefreshing}
                  emptyMessage={config.emptyMessage}
                  imageBreakdown={variant === "leaderboard"}
                />
              )}
            </>
          ) : null}
        </Stack>
      </Box>
    </LocalizationProvider>
  );
}
